import { Tooltip } from '@bp/ui-components';
import classNames from 'classnames';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, MouseEvent, useCallback } from 'react';
import { useConfirm } from '../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useDeleteTimetableCardsMutation } from '../../../types/planung-graphql-client-defs';
import { showUserErrorToast } from '../../../utils/toast';
import { PinboardCard } from '../../PinboardCard/PinboardCard';
import { CardType } from '../../TimetableVersion/graphql/types';
import { useTimetableStore } from '../TimetableProvider';
import styles from './CardPicker.module.scss';
import { buildStackEntries } from './stackEntries';

type CardPickerProps = {
  searchString: string;
};

export const CardPicker: FC<CardPickerProps> = observer(({ searchString }: CardPickerProps) => {
  const pinboardStore = useTimetableStore();
  const context = useMemorizedCacheTag('LESSONS');
  const [, deleteCards] = useDeleteTimetableCardsMutation();
  const { confirm, ConfirmationDialog } = useConfirm();

  // Löschen nur im Ablagestapel der Stecktafel: im Raum-Kontext liegen hier gesteckte Karten.
  const canDelete = pinboardStore.isEditable && pinboardStore.context !== 'rooms';

  const deleteFromStack = async (stack: CardType[], all: boolean) => {
    const deletable = stack.filter((card) => !pinboardStore.lockedCards.has(card.uuid));
    const cards = all ? deletable : deletable.slice(0, 1);
    if (cards.length === 0) return;
    if (
      all &&
      !(await confirm({
        message: t('pinboard.actions.deleteCardsConfirm', {
          count: cards.length,
          subject: cards[0].subject?.name ?? '',
        }),
      }))
    ) {
      return;
    }
    const uuids = cards.map((card) => card.uuid);
    if (pinboardStore.pickedCard && uuids.includes(pinboardStore.pickedCard.cardUuid)) {
      pinboardStore.discardPickedCard();
    }
    const response = await deleteCards({ cardUuids: uuids }, context);
    if (response.error) {
      showUserErrorToast({ error: response.error });
      return;
    }
    pinboardStore.reloadVersionData();
  };

  function handleClick(e: MouseEvent) {
    e.stopPropagation();
    const pickedCard = pinboardStore.pickedCard;
    if (pickedCard) {
      if (pinboardStore.context === 'rooms') {
        pinboardStore.discardPickedCardToRoomsStack();
      } else {
        pinboardStore.discardPickedCardToStack();
      }
    }
  }
  // Aus dem Stapel aufgenommene Karte: ihr Stapel bleibt an seiner Stelle stehen (mit der restlichen
  // Anzahl oder als blasser Platzhalter), sonst rücken die folgenden Stapel nach. Im Raum-Kontext
  // bleibt die Karte beim Aufnehmen ohnehin im Stapel.
  const picked = pinboardStore.pickedCard;
  const pickedFromStackUuid = picked?.fromStack && pinboardStore.context !== 'rooms' ? picked.cardUuid : null;
  const stacks = buildStackEntries(
    pinboardStore.getCardsStack(pickedFromStackUuid ?? undefined),
    pickedFromStackUuid,
    searchString,
  );

  // Gesteckte Position (Wochentag + Name der Startstunde) der Karte – im Raum-Kontext als Tooltip.
  const positionHint = (card: CardType): string | undefined => {
    if (pinboardStore.context !== 'rooms') return undefined;
    const day = pinboardStore.days.find((d) => d.value === card.weekday)?.label;
    const time = pinboardStore.timeGridEntries?.find((e) => e.uuid === card.startTimeGridEntry?.uuid)?.name;
    return [day, time].filter(Boolean).join(', ') || undefined;
  };

  const onCardClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, card: CardType) => {
      event.stopPropagation();
      event.preventDefault();
      if (pinboardStore.pickedCard) {
        pinboardStore.discardPickedCard();
      }
      pinboardStore.pickCard(
        {
          cardUuid: card.uuid,
          cardRows: card.badgeCardRows,
          duration: card.duration ?? 1,
          counter: 1,
          fromStack: true,
          label: card.subject?.shortName ?? '',
          labelColor: card.badgeCardTextColor,
          position: { x: event.clientX, y: event.clientY },
        },
        undefined,
        true,
        event.shiftKey,
      );
    },
    [pinboardStore],
  );

  return (
    <>
      <div className={classNames(styles['card-picker'], 'has-scrollbar')} onClick={(e) => handleClick(e)}>
        {stacks.map((entry) => {
          const { card, cards } = entry;
          if (entry.placeholder) {
            return <PinboardCard key={entry.key} className={styles.placeholder} card={card} passive />;
          }
          const count = cards.length;
          const cardClasses = classNames({
            [styles.multi]: count >= 2,
          });
          const hint = positionHint(card);

          const cardEl = (
            <PinboardCard
              className={cardClasses}
              card={card}
              count={count}
              hideTitle={!!hint}
              onClick={onCardClick}
              onDelete={canDelete ? (all) => void deleteFromStack(cards, all) : undefined}
            />
          );

          // Tooltip (ui-components) ist "teuer" → nur im Raum-Kontext rendern.
          return hint ? (
            <Tooltip key={entry.key} content={hint}>
              {cardEl}
            </Tooltip>
          ) : (
            <Fragment key={entry.key}>{cardEl}</Fragment>
          );
        })}
      </div>
      <ConfirmationDialog />
    </>
  );
});
